import React from 'react';
import { Box } from '@mui/material';

import {
  SalonPublicThemeProvider,
  useSalonPublicTheme,
} from '../../../../context/SalonPublicThemeContext';
import { HIGH_DENSITY_TABLET_MEDIA } from '../../../../utils/responsiveMedia';
import SalonTemplateAbout from '../blocks/SalonTemplateAbout';
import SalonTemplateContactLocation from '../blocks/SalonTemplateContactLocation';
import SalonTemplateGallery from '../blocks/SalonTemplateGallery';
import SalonTemplateIdentity from '../blocks/SalonTemplateIdentity';
import SalonTemplateOpeningHours from '../blocks/SalonTemplateOpeningHours';
import SalonTemplateReviews from '../blocks/SalonTemplateReviews';
import SalonTemplateServices from '../blocks/SalonTemplateServices';
import SalonTemplateTeam from '../blocks/SalonTemplateTeam';


const CercleHeritageTemplate = ({
  viewModel,
  actions,
  mode = 'public',
  viewport = 'desktop',
  direction = 'ltr',
}) => {
  const { config, layout, tokens: theme } = useSalonPublicTheme();
  const identity = viewModel?.identity || {};
  const photos = Array.isArray(viewModel?.photos) ? viewModel.photos : [];
  const reviews = Array.isArray(viewModel?.reviews) ? viewModel.reviews : [];
  const publicState = viewModel?.publicState || {};
  const visibility = layout.tokens.visibility;
  const mobile = viewport === 'mobile';
  const hasServices = Boolean(
    publicState.servicesLoading
    || viewModel?.serviceCategories?.some((category) => (
      Array.isArray(category?.services) && category.services.length > 0
    )),
  );
  const hasTeam = Boolean(
    visibility.team
    && viewModel?.team?.some((member) => member?.id != null),
  );
  const hasReviews = Boolean(
    reviews.length > 0 || publicState.reviewsLoading || publicState.reviewsError,
  );
  const menuTeamLayout = mobile
    ? 'single-column'
    : hasServices && hasTeam
      ? 'shared-menu-team'
      : hasServices ? 'services-only' : 'team-only';
  const darkSectionStyles = Object.fromEntries(
    Object.entries(layout.config.section_styles).map(([sectionKey, section]) => [
      sectionKey,
      {
        title: section.title.color === 'inherit'
          ? { ...section.title, color: theme.salon.onBackground }
          : section.title,
        content: section.content.color === 'inherit'
          ? { ...section.content, color: theme.salon.onBackground }
          : section.content,
      },
    ]),
  );
  const darkLayoutConfig = {
    ...layout.config,
    section_styles: darkSectionStyles,
  };

  return (
    <Box
      data-testid="salon-template-renderer"
      data-salon-template="cercle_heritage"
      data-mode={mode}
      data-viewport={viewport}
      dir={direction}
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        minHeight: 0,
        minWidth: 0,
        width: '100%',
        backgroundColor: theme.salon.background,
        color: theme.salon.onBackground,
      }}
    >
      <SalonTemplateIdentity
        data={{
          ...identity,
          heroPhoto: photos[0]?.src,
          averageRating: viewModel?.averageRating,
          reviewCount: reviews.length,
          isFavorite: viewModel?.capabilities?.isFavorite,
        }}
        variant="dark"
        mode={mode}
        onBack={actions?.onBack}
        onToggleFavorite={actions?.onToggleFavorite}
        onOpenMobileInfo={actions?.onOpenMobileInfo}
        onOpenHeaderInfo={actions?.onOpenHeaderInfo}
        onBook={actions?.onOpenBooking}
        onContact={actions?.onContact}
        clientChatEnabled={identity.raw?.client_chat_enabled}
      />

      <Box
        data-testid="cercle-heritage-scroll-region"
        sx={{
          flex: 1,
          minHeight: mobile ? 'auto' : 0,
          width: '100%',
          overflowY: mobile ? 'visible' : 'auto',
          overflowX: 'hidden',
          [HIGH_DENSITY_TABLET_MEDIA]: { overflowY: 'visible' },
        }}
      >
        <SalonPublicThemeProvider
          config={config}
          layoutConfig={darkLayoutConfig}
          templateKey="cercle_heritage"
          direction={direction}
        >
          <Box
            component="main"
            data-testid="cercle-heritage-content"
            data-layout={mobile ? 'single-column' : 'editorial-club'}
            sx={{
              width: '100%',
              maxWidth: 1360,
              mx: 'auto',
              px: { xs: 2, md: 5 },
              py: { xs: 3, md: 7 },
              boxSizing: 'border-box',
              color: theme.salon.onBackground,
            }}
          >
            {hasServices || hasTeam ? (
              <Box
                data-testid="cercle-heritage-menu-team"
                data-layout={menuTeamLayout}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: !mobile && hasServices && hasTeam
                    ? 'minmax(0, 7fr) minmax(280px, 5fr)'
                    : 'minmax(0, 1fr)',
                  gap: { xs: 3, md: 6 },
                  alignItems: 'start',
                  minWidth: 0,
                }}
              >
                {hasServices ? (
                  <Box sx={{ minWidth: 0 }}>
                    <SalonTemplateServices
                      data={viewModel?.serviceCategories}
                      variant="menu"
                      mode={mode}
                      viewport={viewport}
                      loading={Boolean(publicState.servicesLoading)}
                      onBookService={actions?.onBookService}
                    />
                  </Box>
                ) : null}
                {hasTeam ? (
                  <Box
                    sx={{
                      minWidth: 0,
                      '& [data-salon-text-role="content"] article > .MuiTypography-root': {
                        color: `${theme.salon.onBackground} !important`,
                      },
                      '& [data-salon-text-role="content"] article > .MuiBox-root:first-of-type .MuiTypography-root': {
                        color: `${theme.salon.onPrimary} !important`,
                      },
                      '& [data-salon-text-role="content"] article > .MuiButtonBase-root': {
                        color: `${theme.salon.onPrimary} !important`,
                      },
                    }}
                  >
                    <SalonTemplateTeam
                      data={viewModel?.team}
                      variant="club"
                      visible={visibility.team}
                      mode={mode}
                      onSelectMember={actions?.onSelectTeamMember}
                    />
                  </Box>
                ) : null}
              </Box>
            ) : null}
            <SalonTemplateGallery
              data={photos}
              salonId={identity.id}
              variant="editorial"
              visible={visibility.gallery}
              mode={mode}
              onSelectPhoto={actions?.onSelectPhoto}
              onNavigatePhoto={actions?.onNavigatePhoto}
              selectedIndex={publicState.photoIndex}
            />
            {hasReviews ? (
              <SalonTemplateReviews
                data={{
                  items: reviews,
                  averageRating: viewModel?.averageRating,
                  isAuthenticated: Boolean(publicState.isAuthenticated),
                  isLoading: Boolean(publicState.reviewsLoading),
                  error: publicState.reviewsError || null,
                }}
                variant="editorial"
                visible={visibility.reviews}
                mode={mode}
                onOpenReviewComposer={actions?.onOpenReviewComposer}
                onOpenAllReviews={actions?.onOpenAllReviews}
              />
            ) : null}
            <SalonTemplateOpeningHours
              data={{ hours: viewModel?.openingHours, closures: viewModel?.closures }}
              variant="table"
              visible={visibility.opening_hours}
              markClosuresAsBlock={false}
            />
            {identity.description ? (
              <Box
                sx={{
                  '& .MuiTypography-root': {
                    color: `${theme.salon.onBackground} !important`,
                  },
                }}
              >
                <SalonTemplateAbout data={identity} variant="editorial" />
              </Box>
            ) : null}
            <SalonTemplateContactLocation
              data={identity}
              variant="contact"
              visible={visibility.location}
              mode={mode}
            />
          </Box>
        </SalonPublicThemeProvider>
      </Box>
    </Box>
  );
};

export default CercleHeritageTemplate;
